// REACT
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
// YARN
import { NavigateFunction, useNavigate } from 'react-router-dom';
import {
  GridColDef,
  GridRenderCellParams,
  GridRowId,
  GridRowParams,
  GridRowsProp,
  GridSortModel,
  GridValidRowModel,
} from '@mui/x-data-grid-pro';
import { toast } from 'react-toastify';
import { ThemeProvider } from '@mui/material/styles';
import { Button, DialogActions, DialogContent, DialogTitle, Dialog } from '@mui/material';
import DoneIcon from '@mui/icons-material/Done';
import ClearIcon from '@mui/icons-material/Clear';

// SERVICES
import { CustomSortableHeader } from 'utils/Datagrid/CustomGenericDataGridUtils';
import useDataGridCommonListActions from 'utils/Datagrid/useDataGridCommonListActions';
import { CustomHeaderWithoutSort } from 'utils/Datagrid/CustomCultureDataGridUtils';
import {
  CustomCommonListDataGridThemeWithCursorPointer,
  CustomLogoCell,
} from 'utils/Datagrid/CustomCommonListDataGridUtils';
import CustomCommonListDataGrid from './../../../utils/Datagrid/CustomCommonListDataGrid';
import { removeStructure } from 'services/API/Administration';
// INTERFACES
import IStructureList from 'components/generics/Interface/Api/Response/Structures/IStructureList';
import { IUser } from 'components/generics/Interface/Api/Response/User/IUser';
// THEMES
import './../../generics/commonListDataGrid.scss';
import './StructureDataGrid.scss';
import { customDialogTheme } from 'assets/styles/themes/Dialog/generic_dialog_mui';

type StructureDataGridParams = {
  structures: IStructureList[];
  refreshData: () => void;
  setModal: React.Dispatch<React.SetStateAction<boolean>>;
  setIsEdit: React.Dispatch<React.SetStateAction<boolean>>;
  setEditData: React.Dispatch<React.SetStateAction<GridValidRowModel | null | undefined>>;
};

export const StructureDataGrid = ({
  structures,
  refreshData,
  setModal,
  setIsEdit,
  setEditData,
}: StructureDataGridParams): React.JSX.Element => {
  const { t } = useTranslation();
  const [rows, setRows] = useState<GridRowsProp | []>([]);
  const [dataGridLoading, setDataGridLoading] = useState<boolean>(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState<boolean>(false);
  const [actionId, setActionId] = useState<GridRowId | null>(null);
  const [sortModel, setSortModel] = useState<GridSortModel>([
    {
      field: 'name',
      sort: 'asc',
    },
  ]);
  const navigate: NavigateFunction = useNavigate();
  const thisStructure: GridValidRowModel | undefined = rows.find((row) => row.id === actionId);

  type ProcessedRowType = {
    id: number;
    name: string;
    status: string;
    logo: string | null;
    users: IUser[] | [];
  };

  const processedRows: ProcessedRowType[] = structures?.map((structure) => {
    return {
      id: structure.id,
      name: structure.name,
      status: structure.is_active ? 'active' : 'inactive',
      logo: structure.logo,
      users: structure.users,
      passwordMaxValidity: structure.passwordMaxValidity,
      passwordMinLength: structure.passwordMinLength,
      twoFactorEnabled: structure?.twoFactorEnabled,
      mfaIntervalDays: structure?.mfaIntervalDays,
    };
  });

  useEffect(() => {
    setDataGridLoading(true);
    processedRows.length && setRows(processedRows);
    setDataGridLoading(false);
  }, [structures]);

  const { getActions } = useDataGridCommonListActions(
    processedRows,
    setModal,
    setIsEdit,
    setEditData,
    setOpenDeleteDialog,
    setActionId,
  );

  const columns: GridColDef[] = [
    {
      field: 'name',
      headerName: t('navigation.cooperative'),
      sortable: false,
      headerAlign: 'center',
      align: 'center',
      flex: 1,
      renderHeader: () => (
        <CustomSortableHeader
          setSortModel={setSortModel}
          fieldName={'name'}
          name={t('navigation.cooperative')}
        />
      ),
    },
    {
      field: 'status',
      headerName: t('form.status'),
      sortable: false,
      headerAlign: 'center',
      align: 'center',
      flex: 1,
      renderHeader: () => (
        <CustomSortableHeader
          setSortModel={setSortModel}
          fieldName={'status'}
          name={t('form.status')}
        />
      ),
      renderCell: (params: GridRenderCellParams) => t(`common.status.${params.value}`),
    },
    {
      field: 'logo',
      headerName: t('label.logo'),
      sortable: false,
      headerAlign: 'center',
      align: 'center',
      flex: 1,
      display: 'flex',
      renderCell: (params: GridRenderCellParams) =>
        CustomLogoCell(params.row.logo, params.row.name),
    },
    {
      field: 'actions',
      type: 'actions',
      headerName: t('common.actions'),
      flex: 1,
      minWidth: 160,
      headerAlign: 'center',
      align: 'center',
      cellClassName: 'actions',
      getActions: (params: GridRowParams) => getActions(params.id),
      renderHeader: () => <CustomHeaderWithoutSort name={t('common.actions')} />,
    },
  ];

  const onRowClick = (params: GridRowParams): void => {
    localStorage.setItem('cooperative', params.id.toString());
    localStorage.removeItem('exploitation');
    localStorage.removeItem('sector');
    localStorage.removeItem('isOrganic');
    window.dispatchEvent(new Event('setActiveMainNavBar'));
    navigate('/cooperative/sectors');
  };

  const handleDeleteClick = async (): Promise<void> => {
    setOpenDeleteDialog(false);
    try {
      await removeStructure(thisStructure?.id);
      toast.success(t('success.structureDeleted', { name: thisStructure?.name }));
      refreshData();
    } catch (error) {
      if (error instanceof Error) {
        toast.error(error.message);
      }
    }
  };

  const renderDeleteDialog = (): React.JSX.Element => {
    return (
      <ThemeProvider theme={customDialogTheme}>
        <Dialog maxWidth="md" open={openDeleteDialog}>
          <DialogTitle>{t('common.warning')}</DialogTitle>
          <DialogContent dividers>
            <h3>{t('title.deleteStructure')}</h3>
            <p>{t('message.confirmDeleteStructure')}</p>
            <p>
              <strong>{thisStructure?.name}</strong>. {t('common.continueQuestion')}
            </p>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleDeleteClick}>
              {t('common.delete')} <DoneIcon />
            </Button>
            <Button onClick={() => setOpenDeleteDialog(false)}>
              {t('common.no')} <ClearIcon />
            </Button>
          </DialogActions>
        </Dialog>
      </ThemeProvider>
    );
  };

  return (
    <div>
      <div className="common-list-data-grid structure-data-grid">
        <CustomCommonListDataGrid
          rows={rows}
          columns={columns}
          isLoading={dataGridLoading}
          sortModel={sortModel}
          onRowClick={onRowClick}
          theme={CustomCommonListDataGridThemeWithCursorPointer}
        />
      </div>
      {renderDeleteDialog()}
    </div>
  );
};
